import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { businessesApi } from '@/api/businesses';
import { useBusinessStore } from '@/store/businessStore';

/**
 * For pages that CANNOT work without a business (packages, automations, social
 * accounts, schedule). "All Projects" is not a valid scope there — anything
 * created would be saved with a null business_id and then vanish from every
 * business-scoped list.
 *
 * Behaviour:
 *  - a business is already selected            -> ready
 *  - none selected but the user has exactly 1  -> auto-select it, ready
 *  - none selected and the user has several    -> needsChoice (ask them)
 *  - the user has none at all                  -> needsBusiness (ask them to create one)
 *  - the stored selection isn't in their list  -> clear it (stale localStorage
 *    from a previous account) and fall through to the rules above
 */
export function useRequiredBusiness() {
  const currentBusinessId = useBusinessStore((s) => s.currentBusinessId);
  const setCurrentBusiness = useBusinessStore((s) => s.setCurrentBusiness);

  const { data, isLoading } = useQuery({
    // Canonical key. This was ['businesses','switcher'] — a separate cache of
    // the same data, so this hook re-fetched a list the rail had already
    // loaded. See hooks/useBusinesses.
    queryKey: ['businesses'],
    queryFn: () => businessesApi.list(),
  });
  const businesses = useMemo(() => data?.data || data || [], [data]);

  const owned = businesses.some((b) => String(b.id) === String(currentBusinessId));

  // Drop a stale selection left behind by a different account.
  useEffect(() => {
    if (!isLoading && currentBusinessId && businesses.length && !owned) {
      setCurrentBusiness('');
    }
  }, [isLoading, currentBusinessId, businesses.length, owned, setCurrentBusiness]);

  // Only one business? Then "All Projects" is meaningless here — pick it.
  useEffect(() => {
    if (!isLoading && !currentBusinessId && businesses.length === 1) {
      setCurrentBusiness(businesses[0].id);
    }
  }, [isLoading, currentBusinessId, businesses, setCurrentBusiness]);

  const businessId = owned ? currentBusinessId : null;

  return {
    businessId,
    businesses,
    isLoading,
    ready: !isLoading && !!businessId,
    needsChoice: !isLoading && !businessId && businesses.length > 1,
    needsBusiness: !isLoading && businesses.length === 0,
  };
}

export default useRequiredBusiness;
